import React, { useMemo } from "react";
import { useGetFacilityWholeList } from "../../../../api/cultivations";
import { FacilityType } from "../../../../config/enums/FacilityType.enum";
import { InternalStorageRecord } from "../../../../types/prodcut-data-review/product-data.types";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../../components/ui/Input/FormUnitInput/FormUnitInput";
import EditableTable, { ColumnDef } from "../shared/components/EditableTable";
import { DATA_TYPE_OPTIONS, DEFAULT_FORM_VALUES, ENV_OPTIONS, WEIGHT_UNIT_OPTIONS } from "./constants/storageTable.constants";
import { StorageFormValues } from "./types/storageTable.types";
import { dim, fromFormValues, toFormValues } from "./utils/storageTable.utils";

// ── Props & Component ────────────────────────────────────────────────────────

interface StorageTableProps {
  records: InternalStorageRecord[];
  onSave: (id: string, patch: Partial<InternalStorageRecord>) => void;
  label: string;
  disabled?: boolean;
}

const StorageTable: React.FC<StorageTableProps> = ({ records, onSave, label, disabled = false }) => {
  const { data: facilities } = useGetFacilityWholeList(FacilityType.STORAGE);

  const facilitySelectOptions = useMemo(
    () => (facilities ?? []).map((f: any) => ({ value: f.text as string, label: f.text as string })),
    [facilities]
  );

  const columns = useMemo<ColumnDef<InternalStorageRecord, StorageFormValues>[]>(
    () => [
      {
        id: "name",
        header: "Storage Name",
        cell: r => <span className="tw-font-medium">{r.name || "—"}</span>,
        editCell: ({ control }) => (
          <FormInput control={control} name="name" label="" className="tw-h-8 tw-text-xs" />
        )
      },
      {
        id: "dataType",
        header: "Data Type",
        cell: r => <span>{r.dataType}</span>,
        editCell: ({ control }) => (
          <FormSelect control={control} name="dataType" label="" options={DATA_TYPE_OPTIONS} />
        )
      },
      {
        id: "environment",
        header: "Environment",
        cell: r => <span>{r.environment || "—"}</span>,
        editCell: ({ control }) => (
          <FormSelect
            control={control}
            name="environment"
            label=""
            placeholder="Select"
            options={ENV_OPTIONS}
          />
        )
      },
      {
        id: "warehouseName",
        header: "Warehouse",
        cell: r => <span>{r.warehouseName || "—"}</span>,
        editCell: ({ control }) => (
          <FormSelect
            control={control}
            name="warehouseName"
            label=""
            placeholder="Select"
            options={facilitySelectOptions}
          />
        )
      },
      {
        id: "storageTemperature",
        header: "Temp (°C)",
        headerClassName: "tw-text-right",
        cellClassName: "tw-text-right",
        cell: r =>
          r.storageTemperature === "" ? (
            <span className="tw-text-muted-foreground">—</span>
          ) : (
            <span className="tw-font-mono">{r.storageTemperature}</span>
          ),
        editCell: ({ control }) => (
          <FormInputNumber
            control={control}
            name="storageTemperature"
            label=""
            enableDecimalWithMinus={true}
            className="tw-h-8 tw-text-xs tw-font-mono tw-w-20 tw-ml-auto"
          />
        )
      },
      {
        id: "weight",
        header: "Weight",
        headerClassName: "tw-text-right",
        cellClassName: "tw-text-right",
        cell: r =>
          r.weight === "" ? (
            <span className="tw-text-muted-foreground">—</span>
          ) : (
            <span className="tw-font-mono">
              {r.weight} {r.weightUnit}
            </span>
          ),
        editCell: ({ control }) => (
          <FormUnitInput
            control={control}
            name="weight"
            label=""
            unitName="weightUnit"
            unitOptions={WEIGHT_UNIT_OPTIONS}
          />
        )
      },
      {
        id: "warehouseSize",
        header: "Size (m³)",
        headerClassName: "tw-text-right",
        cellClassName: "tw-text-right",
        cell: r => dim(r.warehouseSize, r.dataType === "Generic"),
        editCell: ({ control, watch }) =>
          watch("dataType") === "Generic" ? (
            <span className="tw-text-muted-foreground/60 tw-italic">N/A</span>
          ) : (
            <FormInputNumber
              control={control}
              name="warehouseSize"
              label=""
              className="tw-h-8 tw-text-xs tw-font-mono tw-w-20 tw-ml-auto"
            />
          )
      },
      {
        id: "electricity",
        header: "Electricity (kWh/hr)",
        headerClassName: "tw-text-right",
        cellClassName: "tw-text-right",
        cell: r => dim(r.electricity, r.dataType === "Generic"),
        editCell: ({ control, watch }) =>
          watch("dataType") === "Generic" ? (
            <span className="tw-text-muted-foreground/60 tw-italic">N/A</span>
          ) : (
            <FormInputNumber
              control={control}
              name="electricity"
              label=""
              className="tw-h-8 tw-text-xs tw-font-mono tw-w-20 tw-ml-auto"
            />
          )
      },
      {
        id: "utilization",
        header: "Utilization (%)",
        headerClassName: "tw-text-right",
        cellClassName: "tw-text-right",
        cell: r => dim(r.utilization, r.dataType === "Generic", "%"),
        editCell: ({ control, watch }) =>
          watch("dataType") === "Generic" ? (
            <span className="tw-text-muted-foreground/60 tw-italic">N/A</span>
          ) : (
            <FormInputNumber
              control={control}
              name="utilization"
              label=""
              rules={{ max: { value: 100, message: "Max utilization is 100%" } }}
              className="tw-h-8 tw-text-xs tw-font-mono tw-w-20 tw-ml-auto"
            />
          )
      },
      {
        id: "storageDays",
        header: "Days",
        headerClassName: "tw-text-right",
        cellClassName: "tw-text-right",
        cell: r => dim(r.storageDays, r.dataType === "Generic"),
        editCell: ({ control, watch }) =>
          watch("dataType") === "Generic" ? (
            <span className="tw-text-muted-foreground/60 tw-italic">N/A</span>
          ) : (
            <FormInputNumber
              control={control}
              name="storageDays"
              label=""
              removeDecimal={true}
              className="tw-h-8 tw-text-xs tw-font-mono tw-w-20 tw-ml-auto"
            />
          )
      }
    ],
    [facilitySelectOptions]
  );

  return (
    <EditableTable
      records={records}
      columns={columns}
      defaultFormValues={DEFAULT_FORM_VALUES}
      toFormValues={toFormValues}
      fromFormValues={fromFormValues}
      onSave={onSave}
      emptyLabel={label}
      disabled={disabled}
    />
  );
};

export default StorageTable;
